import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { openWhatsApp, callMall } from "../utils/callUtils";
import "./Login.css";

const Login = ({ asBottomSheet = false, isOpen = true, onClose }) => {
  const navigate = useNavigate();
  const { sendWhatsAppOTP, login, loginAsGuest } = useAuth();

  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState("971");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState("phone"); // "phone" or "otp"
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (asBottomSheet && isOpen) {
      setPhoneNumber("");
      setCountryCode("971");
      setStep("phone");
      setOtp("");
      setError("");
    }
  }, [asBottomSheet, isOpen]);

  const countryFlags = {
    971: "🇦🇪", // UAE
    1: "🇺🇸", // USA/Canada
    44: "🇬🇧", // UK
    91: "🇮🇳", // India
  };

  const handleSendOTP = async () => {
    if (!phoneNumber || phoneNumber.trim() === "") {
      setError("Please enter a valid phone number");
      return;
    }

    setError("");
    setLoading(true);

    const fullNumber = `${countryCode}${phoneNumber}`;

    const result = await sendWhatsAppOTP(fullNumber);

    setLoading(false);

    if (result.success) {
      setStep("otp");
    } else {
      setError(result.error || "Failed to send OTP. Please try again.");
    }
  };

  const handleWhatsAppClick = () => {
    openWhatsApp("Hey what's going on at the mall today?");
  };

  const handleCallClick = () => {
    callMall();
  };

  const handleOtpSubmit = async () => {
    if (!otp || otp.trim() === "") {
      setError("Please enter the OTP");
      return;
    }

    setError("");
    setLoading(true);

    const fullNumber = `${countryCode}${phoneNumber}`;
    const result = await login(fullNumber, otp);

    setLoading(false);

    if (result.success) {
      localStorage.setItem("phoneNumber", fullNumber);
      if (asBottomSheet) {
        if (onClose) {
          onClose();
        }
      } else {
        navigate("/");
      }
    } else {
      setError(result.error || "Invalid OTP. Please try again.");
    }
  };

  const handleSkip = async () => {
    setLoading(true);
    const result = await loginAsGuest();
    setLoading(false);

    if (result.success) {
      if (asBottomSheet && onClose) {
        onClose();
        return;
      }
      navigate("/");
    } else {
      setError(result.error || "Failed to login as guest. Please try again.");
    }
  };

  if (asBottomSheet && !isOpen) {
    return null;
  }

  return (
    <div className={asBottomSheet ? "login-sheet-overlay" : ""}>
      {asBottomSheet && (
        <button
          type="button"
          className="login-sheet-backdrop"
          aria-label="Close login"
          onClick={handleSkip}
        />
      )}
      <div
        className={`login-container ${asBottomSheet ? "login-container-sheet" : ""}`}
      >
        {asBottomSheet && <div className="login-sheet-handle" />}
        <div className="login-header">
          <h2 className="login-heading">
            {step === "phone" ? "Connect your number?" : "Enter OTP"}
          </h2>
        </div>

        <div className="login-content">
          <p className="login-description">
            {step === "phone" ? (
              <>
                If you do so, your <i>Personal Mall Concierge</i> will be able
                to share the info you want directly on your <b>WhatsApp</b> or{" "}
                <b>call you</b> back when you say so!
              </>
            ) : (
              <>
                We've sent a verification code to{" "}
                <b>
                  {countryCode}
                  {phoneNumber}
                </b>
                .
              </>
            )}
          </p>

          {error && <div className="error-message">{error}</div>}

          {step === "phone" ? (
            <>
              <div className="phone-input-section">
                <label className="phone-label">Mobile Number</label>
                <div className="phone-input-container">
                  <div className="country-code-selector">
                    <span className="flag">
                      {countryFlags[countryCode] || "🇦🇪"}
                    </span>
                    <select
                      className="country-code"
                      value={countryCode}
                      onChange={(e) => setCountryCode(e.target.value)}
                      disabled={loading}
                    >
                      <option value="971">971</option>
                      <option value="1">1</option>
                      <option value="44">44</option>
                      <option value="91">91</option>
                    </select>
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="dropdown-arrow"
                    >
                      <path
                        d="M3 4.5L6 7.5L9 4.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <input
                    type="tel"
                    className="phone-input"
                    placeholder="Enter mobile number"
                    value={phoneNumber}
                    onChange={(e) =>
                      setPhoneNumber(e.target.value.replace(/\D/g, ""))
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !loading && phoneNumber) {
                        handleSendOTP();
                      }
                    }}
                    disabled={loading}
                  />
                </div>
                <button
                  className="send-otp-button"
                  onClick={handleSendOTP}
                  disabled={loading || !phoneNumber}
                >
                  {loading ? "Sending..." : "Send WhatsApp OTP"}
                </button>
              </div>

              <div className="or-divider"> - OR - </div>
              <div className="login-action-buttons">
                <button
                  className="login-action-button whatsapp-button"
                  onClick={handleWhatsAppClick}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="button-icon"
                  >
                    <path
                      d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"
                      fill="currentColor"
                    />
                    <path
                      d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2.546 20.2l3.18-.836A9.955 9.955 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    />
                  </svg>
                  <span className="button-text">
                    <span className="button-main-text">
                      Just WhatsApp the mall now
                    </span>
                  </span>
                </button>

                <button
                  className="login-action-button call-button"
                  onClick={handleCallClick}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="button-icon"
                  >
                    <path
                      d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span className="button-text">
                    <span className="button-main-text">
                      Call & talk to mall concierge
                    </span>
                  </span>
                </button>
              </div>

              <div className="skip-button-container">
                <button
                  className="skip-button"
                  onClick={handleSkip}
                  disabled={loading}
                >
                  Skip for now
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="phone-input-section">
                <label className="phone-label">Enter OTP</label>
                <div className="otp-input-container">
                  <input
                    type="text"
                    className="otp-input"
                    placeholder="Enter 6-digit code"
                    value={otp}
                    onChange={(e) =>
                      setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !loading && otp.length >= 4) {
                        handleOtpSubmit();
                      }
                    }}
                    maxLength="6"
                    disabled={loading}
                    autoFocus
                  />
                </div>
              </div>

              <button
                className="submit-button"
                onClick={handleOtpSubmit}
                disabled={loading || otp.length < 4}
              >
                {loading ? "Verifying..." : "Verify & Sign In"}
              </button>

              <button
                className="resend-button"
                onClick={handleSendOTP}
                disabled={loading}
              >
                Resend OTP
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
